import type { SxProps, Theme } from '@mui/material';
import { BRAND_LUXE_PRIMARY, BRAND_SURFACE_CONTAINER_HIGH } from '../../../../pages/BrandDashboard/brandDashboardTokens';
import { brandSubmissionHeaderSx, brandSubmissionMobileLabelSx } from './brandSubmissionCardLayout';
import { midnight } from '../../../../pages/BrandDashboard/contestWizard/midnightStyles';

/** Desktop row from lg (1200px) — below uses premium compact card. */
export const BRAND_SALES_DESKTOP_MIN_PX = 1200;

const desktopMq = `@media (min-width: ${BRAND_SALES_DESKTOP_MIN_PX}px)`;

export const brandSalesGridColumns = {
    lg: 'minmax(148px, 1.5fr) minmax(88px, 1.05fr) minmax(52px, 0.8fr) minmax(48px, 0.75fr) minmax(100px, 1fr)',
} as const;

export const brandSalesCompactVisibleSx: SxProps<Theme> = {
    display: 'block',
    [desktopMq]: {
        display: 'none',
    },
};

export const brandSalesDesktopVisibleSx: SxProps<Theme> = {
    display: 'none',
    [desktopMq]: {
        display: 'block',
    },
};

export const brandSalesDesktopHeaderVisibleSx: SxProps<Theme> = {
    display: 'none',
    [desktopMq]: {
        display: 'grid',
    },
};

export const brandSalesGlassCardSx: SxProps<Theme> = {
    position: 'relative',
    overflow: 'hidden',
    maxWidth: '100%',
    boxSizing: 'border-box',
    borderRadius: '16px',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    bgcolor: 'rgba(26, 26, 26, 0.85)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    transition: 'border-color 0.3s ease, box-shadow 0.3s ease, transform 0.3s ease',
    '&:hover': {
        borderColor: 'rgba(255, 201, 101, 0.35)',
        boxShadow: '0 12px 28px rgba(0, 0, 0, 0.35)',
        transform: 'translateY(-2px)',
    },
};

export const brandSalesCompactCardSx: SxProps<Theme> = {
    ...brandSalesGlassCardSx,
    p: { xs: 3, sm: 3.5 },
    maxWidth: { xs: '100%', sm: 400 },
    mx: { xs: 0, sm: 'auto' },
    width: '100%',
};

export const brandSalesAmbientGlowTopSx: SxProps<Theme> = {
    position: 'absolute',
    top: -96,
    right: -96,
    width: 192,
    height: 192,
    borderRadius: '50%',
    bgcolor: BRAND_LUXE_PRIMARY,
    opacity: 0.1,
    filter: 'blur(80px)',
    pointerEvents: 'none',
};

export const brandSalesAmbientGlowBottomSx: SxProps<Theme> = {
    position: 'absolute',
    bottom: -96,
    left: -96,
    width: 192,
    height: 192,
    borderRadius: '50%',
    bgcolor: BRAND_LUXE_PRIMARY,
    opacity: 0.05,
    filter: 'blur(80px)',
    pointerEvents: 'none',
};

export const brandSalesDividerSx: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    width: '100%',
    opacity: 0.2,
    my: 1,
};

export const brandSalesCardGridSx: SxProps<Theme> = {
    display: 'grid',
    gridTemplateColumns: brandSalesGridColumns.lg,
    columnGap: 2,
    alignItems: 'center',
    px: 3,
    py: 2,
    width: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
};

export const brandSalesHeaderSx: SxProps<Theme> = {
    ...brandSubmissionHeaderSx,
    whiteSpace: 'normal',
    lineHeight: 1.15,
};

export const brandSalesStackedHeaderSx = (align: 'start' | 'center'): SxProps<Theme> => ({
    ...brandSalesHeaderSx,
    display: 'flex',
    flexDirection: 'column',
    alignItems: align === 'start' ? 'flex-start' : 'center',
    justifyContent: 'flex-end',
    textAlign: align,
    justifySelf: align,
    minHeight: 28,
    gap: 0.15,
});

export const brandSalesFieldLabelSx: SxProps<Theme> = {
    ...brandSubmissionMobileLabelSx,
    fontFamily: 'Inter, sans-serif',
    fontSize: 10,
    fontWeight: 500,
    letterSpacing: '0.2em',
    textTransform: 'uppercase',
    color: `${midnight.onSurfaceVariant}`,
    opacity: 0.6,
    textAlign: 'center',
    display: 'block',
    mb: 0.5,
};

export const brandSalesCurrencyChipSx: SxProps<Theme> = {
    bgcolor: BRAND_SURFACE_CONTAINER_HIGH,
    px: 2.5,
    py: 0.5,
    borderRadius: '8px',
    border: '1px solid rgba(255, 255, 255, 0.05)',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    maxWidth: '100%',
};

export const brandSalesCellCenterSx: SxProps<Theme> = {
    justifySelf: 'center',
    alignItems: 'center',
    textAlign: 'center',
    minWidth: 0,
};

export const brandSalesCellStartSx: SxProps<Theme> = {
    justifySelf: 'start',
    alignItems: 'flex-start',
    textAlign: 'left',
    minWidth: 0,
    width: '100%',
};
